import { toast_error } from '@/libs/toast-wrappers';
import { errorlog } from '@/libs/error-logs';
import {
  ERROR_CREATING_CASE,
  ERROR_DOWNLOADING_REPORT,
  ERROR_FETCH_CASES,
  ERROR_GET_GRADE_CASES,
  ERROR_MESSAGE_REPORT_GENERATION,
} from '@/utils/messages';
import { toError } from '@/utils/errors';

import { DELETE, GET, GET_FILE, POST, PUT } from './base/index';
import { ENDPOINTS } from './endpoints';
import { Grade } from './grade';
import { PATCH } from './base/base-request';
import { StandardResponse } from './standard-response';

export type Case = {
  CSME: number;
  user_email: string;
  processing_time_seconds: number;
  clinic_id: string;
  result: number;
  date_created: string;
  date_submitted: string;
  DR: number;
  camera_manufacturer_model: string;
  date_resulted: string;
  case_id: string;
  patient_id: string;
  site_id: string;
  hasConflictingGrades_bool: boolean;
  grades?: Grade[];
  location?: {
    city: string;
    country: string;
    province_or_state: string;
  };
  claimed_by: string;
  claimed_time: string;
  indications?: { id: string; indication: string; method?: string }[];
};

type GetCasesParams = {
  clinic?: string;
  patient?: string;
  patient_name?: string;
  user?: string;
  limit?: number;
  expand?: ('grades' | 'location')[];
};

export async function getCases(params?: GetCasesParams): Promise<Case[]> {
  try {
    const query = {
      clinic: params?.clinic ?? 'all',
      patient: params?.patient ?? 'all',
      user: params?.user ?? 'all',
      limit: encodeURIComponent(params?.limit ?? 1000),
      ...(params?.expand ? { expand: params.expand.join(',') } : {}),
      ...(params?.patient_name ? { patient_name: params.patient_name } : {}),
    };
    const response = await GET<Case[]>(ENDPOINTS.CASES, {
      query,
    });
    if ('cases' in response) {
      return response.cases as Case[];
    }
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCases', 'Error fetching cases');
    toast_error(ERROR_FETCH_CASES);
    return Promise.reject(toError(error));
  }
}

type GetGradeParams = Omit<GetCasesParams, 'expand'> & {
  assignedToGrader: string;
};

/**
 * Fetches the cases for the user
 * @param params the parameters for the request
 * @returns the cases for the user
 */
export async function getGradeCases(params: GetGradeParams) {
  try {
    const query = {
      clinic: params?.clinic ?? 'all',
      patient: params?.patient ?? 'all',
      user: params?.user ?? 'all',
      assignedToGrader: params?.assignedToGrader,
      expand: 'grades,location',
    };
    const response = await GET<Case[]>(ENDPOINTS.CASES, {
      query,
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getGradeCases', 'Error fetching cases for grading');
    toast_error(ERROR_GET_GRADE_CASES);
    return Promise.reject(toError(error));
  }
}

type CaseResponseList = { result: string[] };
type CaseResponseScore = { result: string };
type CaseResponse = CaseResponseList | CaseResponseScore;

export async function getCase(caseId: string, type = 'list', include_layers = false) {
  try {
    const response = await GET<CaseResponse>(ENDPOINTS.CASE, {
      query: { type, include_layers: include_layers.toString() },
      parameters: { caseId },
    });
    return response.result;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCase', 'Error fetching case');
    toast_error('Error fetching case');
    return Promise.reject(toError(error));
  }
}

export type CaseImages = {
  image: string;
  url: string;
};
export type CaseToGrade = Case & { images: CaseImages[] };
type CaseToGradeResponse = StandardResponse<CaseToGrade>;

export async function getCaseToGrade(caseId: string) {
  try {
    const response = await GET<CaseToGradeResponse>(ENDPOINTS.CASE, {
      query: { grading: 'true' },
      parameters: { caseId },
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCaseToGrade', 'Error fetching case for grading');
    toast_error('Error fetching case');
    return Promise.reject(toError(error));
  }
}

type CaseDetailResponse = StandardResponse<Case>;

/**
 * Fetches a single case by ID with full case metadata
 * @param caseId - The case ID to fetch
 * @returns Promise<StandardResponse<Case>> - The case data wrapped in standard response
 */
export async function getCaseById(caseId: string) {
  try {
    const response = await GET<CaseDetailResponse>(ENDPOINTS.CASE, {
      query: { grading: 'true' },
      parameters: { caseId },
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCaseById', 'Error fetching case by ID');
    toast_error('Error fetching case details');
    return Promise.reject(toError(error));
  }
}

export async function getCaseImage(imgPath: string) {
  try {
    const response = await GET_FILE(imgPath);
    const path = imgPath.split('/').pop();
    const file = new File([response], path ?? imgPath, { type: response.type });

    return { path: imgPath, file };
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCaseImage', 'Error fetching case image');
    toast_error('Error fetching case image');
    return Promise.reject(toError(error));
  }
}

export async function getCaseFile(caseId: string, filePath: string) {
  try {
    const fullEndpoint = ENDPOINTS.CASE_FILE.replace('{caseId}', caseId).replace(
      '{filePath}',
      filePath
    );
    const response = await GET_FILE(fullEndpoint);
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCaseFile', 'Error fetching case');
    toast_error(ERROR_DOWNLOADING_REPORT);
    return Promise.reject(toError(error));
  }
}

export async function putCaseFile(caseId: string, filePath: string, file: File) {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await PUT<File>(ENDPOINTS.CASE_FILE, formData, {
      parameters: { caseId, filePath },
      // headers: { 'Content-Type': fileType ?? 'application/octet-stream' },
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/putCaseFile', 'Error uploading case file');
    toast_error('Error uploading case file');
    return Promise.reject(toError(error));
  }
}

export async function postGenerateReport(caseId: string, reportFileName: string) {
  try {
    const response = await POST(
      ENDPOINTS.GENERATE_REPORT,
      {}, // empty body
      {
        parameters: { caseId, dl_file_name: reportFileName },
      }
    );
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/postGenerateReport', 'Error generating report');
    toast_error(ERROR_MESSAGE_REPORT_GENERATION);
    return Promise.reject(toError(error));
  }
}

type CaseForm = {
  patient_id: string;
  site_id: string;
  camera: string;
  patientName?: string;
  gender?: string;
  ethnicity?: string;
  hasDiabetes?: string;
  diabetesType?: string;
  yearDiagnosed?: string;
  eyeHistory?: string;
  notes?: string;
  orderType?: 'TH';
  // Optional indications used during case creation/editing
  indications?: { id: string; indication: string; method?: string }[];
};

export async function postCase(newCase: CaseForm) {
  try {
    const response = await POST<{ case: string }>(ENDPOINTS.CASES, newCase);
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/postCase', 'Error creating case');
    toast_error(ERROR_CREATING_CASE);
    return Promise.reject(toError(error));
  }
}

/**
 * Partially updates a case by ID. Intended for editing metadata before submission.
 */
export async function patchCase(caseId: string, payload: Partial<CaseForm>) {
  try {
    const response = await PATCH(ENDPOINTS.CASE, payload, { parameters: { caseId } });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/patchCase', 'Error updating case');
    toast_error('Error updating case');
    return Promise.reject(toError(error));
  }
}

export async function postCaseInit(caseId: string, caseType: string, quality: string) {
  try {
    const response = await POST(
      ENDPOINTS.CASE,
      {}, // it use an empty body
      {
        parameters: { caseId },
        query: { type: caseType, quality },
      }
    );
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/postCaseInit', 'Error initializing case');
    toast_error('Error initializing case');
    return Promise.reject(toError(error));
  }
}

export async function deleteFile(caseId: string, filePath: string) {
  try {
    const response = await DELETE(ENDPOINTS.CASE_FILE, {
      parameters: { caseId, filePath },
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/deleteFile', 'Error deleting file');
    toast_error('Error deleting file');
    return Promise.reject(toError(error));
  }
}

export async function setCaseClaimed(caseId: string, userEmail: string) {
  try {
    const response = await PATCH(
      ENDPOINTS.CASE_CLAIM,
      { user_id: userEmail },
      { parameters: { caseId } }
    );
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/setCaseClaimed', 'Error setting case claimed');
    return Promise.reject(toError(error));
  }
}

export async function getCaseImageLayerList(caseId: string) {
  try {
    const response = await GET<{ data: { layer: string; url: string }[] }>(
      ENDPOINTS.CASE_IMAGES_LAYER_URL,
      { parameters: { caseId }, enableErrorOn404: false }
    );
    return response;
  } catch (e) {
    const error = e as Error;
    errorlog(error, 'src/api/cases/getCaseImageLayerList', 'Error fetching case image layers');
    if (error?.message.includes('404')) {
      // If the error is a 404, it means there are no layers for this case
      return { data: [] };
    }
    toast_error('Error fetching case image segmentation images');
    return Promise.reject(error);
  }
}

export type CaseImageData = {
  case_id: string;
  image_filename: string;
  lesion_segment_vals?: {
    Area: string;
    'Bright Lesions': string;
    'Near Fovea': string;
    Quadrants: string;
    'Red Lesions': string;
  };
  optic_disc_segment_vals?: {
    Cup_to_Disc_Ratio: string;
    OD_Diam_Ratio: string;
  };
  vessel_segment_vals?: {
    'A/V Ratio': string;
    Fractals: string;
    Tortuosity: string;
    'Vessel Density': string;
  };
};

export async function getCaseImagesData(caseId: string) {
  try {
    const response = await GET<{ data: CaseImageData[] }>(ENDPOINTS.CASE_IMAGES_DATA, {
      parameters: { caseId },
    });
    return response.data;
  } catch (error) {
    errorlog(error as Error, 'src/api/cases/getCaseImagesData', 'Error fetching case images data');
    toast_error('Error fetching case images data');
    return Promise.reject(toError(error));
  }
}
